import { useEffect } from "react";
import { Navigate, Outlet, useLocation, useParams } from "react-router-dom";
import { ReactQueryDevtools } from "@tanstack/react-query-devtools";
import AppSideBar from "./app-sidebar";
import ScrollToTop from "./scroll-to-top";
import ThreeDotsLoader from "./three-dots-loader";
import useActiveRegions from "@/features/regions/hooks/use-active-regions";
import {
  ALL_REGIONS_CODE,
  getStoredRegion,
  regionPath,
  setStoredRegion,
} from "@/features/regions/constants";

// Layout under the /:region segment: the URL region is the source of truth.
// Persists valid regions to localStorage (for the return redirect) and, when
// the first URL segment isn't a real region (e.g. a region-less `/users`),
// redirects ONCE to /{lastRegion}/<full path> — preserving the page.
export default function RegionLayout() {
  const { region } = useParams();
  const location = useLocation();
  const { data: regions, isLoading, isError } = useActiveRegions();

  const isKnown =
    region === ALL_REGIONS_CODE ||
    (!!regions && regions.some((r) => r.code === region));

  // Only ever store a confirmed-valid region (never pollute with a page name).
  useEffect(() => {
    if (region && isKnown) {
      setStoredRegion(region);
    }
  }, [region, isKnown]);

  // Gate render until the region list resolves: prevents the nested index
  // <Navigate> from firing mid-load and avoids a wasted ?region=<garbage> fetch.
  if (isLoading) {
    return (
      <div className="flex min-h-screen w-full items-center justify-center">
        <ThreeDotsLoader />
      </div>
    );
  }

  // Region-less / unknown first segment: treat the whole path as in-region and
  // redirect once to a validated fallback region (last-opened, else "all"),
  // keeping the full path + query. Skipped on error (render best-effort).
  if (!isError && regions && !isKnown) {
    const stored = getStoredRegion();
    const fallback =
      stored && (stored === ALL_REGIONS_CODE || regions.some((r) => r.code === stored))
        ? stored
        : ALL_REGIONS_CODE;
    return (
      <Navigate
        to={regionPath(fallback, location.pathname) + location.search}
        replace
      />
    );
  }

  return (
    <AppSideBar>
      <ScrollToTop />
      <Outlet />
      <ReactQueryDevtools />
    </AppSideBar>
  );
}
